/**
 * Support ticket writes, straight to ant.
 *
 * Reads already come from ant through the Relay hooks in
 * `hooks/supportTickets/query/V2`. Writes cannot: every Relay mutation in
 * this app is currently stubbed to `null as any`, with a note saying the
 * mutations were pulled from the schema. Rather than revive that machinery
 * for one form, this uses the plain graphql-request client the rest of the
 * api layer already has.
 */

import { getGraphQLClient } from './client';

// A plain template literal on purpose. `gql` from graphql-request is only a
// tagging helper for editor tooling, it ships as ESM, and jest cannot
// transform it here — importing it fails the suite for no runtime gain.
const CREATE_SUPPORT_TICKET = `
  mutation CreateSupportTicket($input: CreateSupportTicketInput) {
    createSupportTicket(input: $input) {
      result {
        id
        oldId
        content
        state
        created
      }
      errors {
        code
        fields
        message
        shortMessage
      }
    }
  }
`;

export interface CreateSupportTicketInput {
  /**
   * The rider's phone. The column is named `userId` but the relationship
   * resolves against `users.phone`, so this is a phone number, not an id.
   */
  userOldId?: string;
  /** The system's numeric old id, as held in admin credentials. */
  systemOldId?: string;
  content?: string;
  type?: number;
  subtype?: string;
  rentalOldId?: string;
  hardwareOldId?: string;
  deviceId?: string;
  moduleId?: string;
  tags?: string[];
}

export interface CreatedSupportTicket {
  id: string;
  oldId: string | null;
  content: string | null;
  state: number | null;
  created: string | null;
}

interface MutationError {
  code?: string | null;
  fields?: string[] | null;
  message?: string | null;
  shortMessage?: string | null;
}

interface CreateSupportTicketResponse {
  createSupportTicket: {
    result: CreatedSupportTicket | null;
    errors: MutationError[];
  };
}

/**
 * Open a support ticket on the rider's behalf.
 *
 * ant sets `state` to 0 and stamps `created`; neither is accepted here. A
 * ticket that arrived with a null state would never match the Customer
 * Support list's `state IN (...)` filter and CS would not see what they just
 * created.
 *
 * Rejects with the first mutation error so `CustomAsyncToast` shows the
 * reason rather than a generic failure.
 */
export const createSupportTicket = async (
  input: CreateSupportTicketInput
): Promise<CreatedSupportTicket> => {
  const client = getGraphQLClient();

  const response = await client.request<CreateSupportTicketResponse>(
    CREATE_SUPPORT_TICKET,
    { input }
  );

  const { result, errors } = response.createSupportTicket;

  if (errors?.length) {
    const first = errors[0];
    throw new Error(first.shortMessage || first.message || 'Could not create the ticket');
  }

  if (!result) {
    throw new Error('Could not create the ticket');
  }

  return result;
};
